import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { Card, Form, Button, Alert, Row, Col, Spinner } from 'react-bootstrap';
import RecaptchaComponent from '../components/RecaptchaComponent';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SEO from '../components/SEO';

/**
 * OAuth Authorization Page
 * This page handles the OAuth 2.0 authorization flow for desktop applications.
 * Users sign in here and are redirected back to the desktop app with an authorization code.
 */
const OAuthAuthorize = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  
  // Debug logging
  console.log('OAuthAuthorize component loaded');
  console.log('Search params:', Object.fromEntries(searchParams.entries()));
  
  // OAuth parameters from URL
  const responseType = searchParams.get('response_type');
  const clientId = searchParams.get('client_id');
  const redirectUri = searchParams.get('redirect_uri');
  const state = searchParams.get('state');
  const codeChallenge = searchParams.get('code_challenge');
  const codeChallengeMethod = searchParams.get('code_challenge_method');
  const deviceId = searchParams.get('device_id');

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaError, setRecaptchaError] = useState('');
  const [isValidatingParams, setIsValidatingParams] = useState(true);
  const [validationError, setValidationError] = useState('');
  
  // Already signed-in user state
  const [readyToContinue, setReadyToContinue] = useState(false);
  const [autoRedirected, setAutoRedirected] = useState(false);

  // Validate OAuth parameters
  useEffect(() => {
    const validateOAuthParams = () => {
      const requiredParams = ['response_type', 'client_id', 'redirect_uri', 'state', 'code_challenge', 'device_id'];
      const missingParams = requiredParams.filter(param => !searchParams.get(param));
      
      if (missingParams.length > 0) {
        setValidationError(`Missing required OAuth parameters: ${missingParams.join(', ')}`);
        setIsValidatingParams(false);
        return;
      }

      // Validate response_type
      if (responseType !== 'code') {
        setValidationError('Invalid response_type. Must be "code".');
        setIsValidatingParams(false);
        return;
      }

      // Validate client_id
      if (clientId !== 'osailu-desktop') {
        setValidationError('Invalid client_id. Must be "osailu-desktop".');
        setIsValidatingParams(false);
        return;
      }

      // Validate redirect_uri
      if (redirectUri !== 'osailu://oauth/callback') {
        setValidationError('Invalid redirect_uri. Must be "osailu://oauth/callback".');
        setIsValidatingParams(false);
        return;
      }

      // Validate code_challenge_method
      if (codeChallengeMethod && codeChallengeMethod !== 'S256') {
        setValidationError('Invalid code_challenge_method. Must be "S256".');
        setIsValidatingParams(false);
        return;
      }

      setIsValidatingParams(false);
    };

    validateOAuthParams();
  }, [searchParams, responseType, clientId, redirectUri, codeChallengeMethod]);

  // Check if user is already authenticated
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !isValidatingParams && !validationError) {
        // User is already authenticated, show continue option instead of immediate redirect
        setReadyToContinue(true);
        
        // Auto-continue after 1500ms to give desktop app time to persist PKCE data
        const autoRedirectTimer = setTimeout(async () => {
          if (!autoRedirected) {
            setAutoRedirected(true);
            await handleOAuthRedirect(user);
          }
        }, 1500);
        
        return () => clearTimeout(autoRedirectTimer);
      }
    });

    return () => unsubscribe();
  }, [isValidatingParams, validationError, autoRedirected]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started');
    setLoading(true);
    setError('');

    try {
      // Execute reCAPTCHA verification first
      let currentRecaptchaToken = recaptchaToken;
      
      if (!currentRecaptchaToken) {
        // Try to execute reCAPTCHA if we don't have a token
        if (executeRecaptcha) {
          try {
            console.log('Executing reCAPTCHA...');
            currentRecaptchaToken = await executeRecaptcha('submit');
            if (currentRecaptchaToken) {
              console.log('reCAPTCHA token received');
              setRecaptchaToken(currentRecaptchaToken);
            }
          } catch (recaptchaError) {
            console.error('reCAPTCHA execution failed:', recaptchaError);
            setError('reCAPTCHA verification failed. Please try again.');
            setLoading(false);
            return;
          }
        } else {
          console.warn('reCAPTCHA not available');
        }
      }

      if (!currentRecaptchaToken) {
        setError('reCAPTCHA verification required');
        setLoading(false);
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      
      // Redirect to desktop app with authorization code
      handleOAuthRedirect(userCredential.user);
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Handle specific Firebase errors
      switch (err.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password');
          break;
        case 'auth/invalid-email':
          setError('Please enter a valid email address');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later');
          break;
        default:
          setError('Login failed. Please check your credentials and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthRedirect = async (user) => {
    try {
      console.log('handleOAuthRedirect called with PKCE parameters:', {
        deviceId, codeChallenge, codeChallengeMethod, state
      });
      
      // Check if email is verified
      if (!user.emailVerified) {
        setError('Please verify your email address before authorizing the desktop app. Check your inbox for a verification link.');
        return;
      }
      
      // Get Firebase ID token for authentication
      const idToken = await user.getIdToken();
      
      // Call the server's OAuth authorize endpoint to get the authorization code
      // This endpoint will use the PKCE parameters from the URL to generate the correct code
      const response = await fetch('https://osailu-api.osailu.com/api/oauth/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          deviceId: deviceId,
          codeChallenge: codeChallenge,
          codeChallengeMethod: codeChallengeMethod,
          state: state
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const authData = await response.json();
      const authCode = authData.authorizationCode;
      
      console.log('Received authorization data from server:', authData);
      
      // Build redirect URL with server-generated authorization code
      const redirectUrl = new URL(redirectUri);
      redirectUrl.searchParams.set('code', authCode);
      redirectUrl.searchParams.set('state', state);
      
      // Try to redirect to desktop app
      try {
        window.location.href = redirectUrl.toString();
        
        // If we get here, the redirect might have failed
        // Redirect to home screen instead of showing popup
        setTimeout(() => {
          navigate('/');
        }, 2000);
        
      } catch (redirectError) {
        console.error('Redirect failed:', redirectError);
        // Redirect to home screen instead of showing popup
        navigate('/');
      }
      
    } catch (error) {
      console.error('OAuth redirect error:', error);
      setError('Failed to complete authorization. Please try again.');
    }
  };



  const handleRecaptchaVerify = (token) => {
    setRecaptchaToken(token);
    setRecaptchaError('');
    if (error && error.includes('reCAPTCHA')) {
      setError('');
    }
  };

  const handleRecaptchaError = (error) => {
    setRecaptchaError(error);
    setRecaptchaToken(null);
  };

  const handleContinueClick = async () => {
    if (autoRedirected) return; // Prevent double-clicks
    
    setAutoRedirected(true);
    const user = auth.currentUser;
    if (user) {
      await handleOAuthRedirect(user);
    }
  };

  if (isValidatingParams) {
    return (
      <>
        <SEO
          title="OAuth Authorization - Osailu"
          description="Authorize Osailu desktop application to access your account."
          url="/oauth/authorize"
          noindex={true}
        />
        <div style={{ 
          minHeight: 'calc(100vh - 120px)', 
          backgroundColor: 'var(--bg-color)',
          color: 'var(--text-color)',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          padding: '20px 0'
        }}>
        <div className="container mt-5">
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              <Card>
                <Card.Body className="text-center">
                  <Spinner animation="border" className="mb-3" />
                  <p>Validating authorization request...</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        </div>
      </>
    );
  }

  if (validationError) {
    return (
      <>
        <SEO
          title="OAuth Authorization Error - Osailu"
          description="OAuth authorization error occurred."
          url="/oauth/authorize"
          noindex={true}
        />
        <div style={{ 
          minHeight: 'calc(100vh - 120px)', 
          backgroundColor: 'var(--bg-color)',
          color: 'var(--text-color)',
          transition: 'background-color 0.3s ease, color 0.3s ease',
          padding: '20px 0'
        }}>
        <div className="container mt-5">
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              <Card>
                <Card.Header className="text-center">
                  <h3>Authorization Error</h3>
                </Card.Header>
                <Card.Body>
                  <Alert variant="danger">
                    {validationError}
                  </Alert>
                  <div className="text-center">
                    <Button variant="primary" onClick={() => navigate('/')}>
                      Return to Home
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="OAuth Authorization - Osailu"
        description="Authorize Osailu desktop application to access your account."
        url="/oauth/authorize"
        noindex={true}
      />
      <div style={{ 
        minHeight: 'calc(100vh - 120px)', 
        backgroundColor: 'var(--bg-color)',
        color: 'var(--text-color)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
        padding: '20px 0'
      }}>
      <div className="container mt-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card>
            <Card.Header className="text-center">
              <h3>Authorize Osailu Desktop</h3>
              <p className="text-muted mb-0">Sign in to authorize the desktop application</p>
            </Card.Header>
            <Card.Body>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              {readyToContinue ? (
                // Show Continue button for already signed-in users
                <div>
                  <Alert variant="success" className="mb-3">
                    You are already signed in. Click Continue to authorize the desktop app.
                  </Alert>
                  <Button
                    variant="primary"
                    className="w-100 mb-3"
                    onClick={handleContinueClick}
                    disabled={autoRedirected}
                    style={{ cursor: autoRedirected ? 'not-allowed' : 'pointer' }}
                  >
                    {autoRedirected ? 'Redirecting...' : 'Continue'}
                  </Button>
                </div>
              ) : (
                // Show login form for users who need to sign in
                <>
                  <Form onSubmit={handleLogin}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="Enter your email"
                        required
                        autoComplete="email"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        placeholder="Enter your password"
                        required
                        autoComplete="current-password"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <RecaptchaComponent
                        onVerify={handleRecaptchaVerify}
                        onError={handleRecaptchaError}
                        siteKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                      />
                      {recaptchaError && (
                        <div className="text-danger small mt-1">
                          {recaptchaError}
                        </div>
                      )}
                    </Form.Group>

                    <Button
                      variant="primary"
                      type="submit"
                      className="w-100 mb-3"
                      disabled={loading}
                      style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                    >
                      {loading ? 'Authorizing...' : 'Authorize Desktop App'}
                    </Button>
                  </Form>

                  <div className="text-center">
                    <p className="mb-2">
                      <a href="/forgot-password" className="text-decoration-none">
                        Forgot your password?
                      </a>
                    </p>
                    <p className="mb-0">
                      Don't have an account?{' '}
                      <a href="/signup" className="text-decoration-none">
                        Create one here
                      </a>
                    </p>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
    </div>
    </>
  );
};

const OAuthAuthorizeWithRecaptcha = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  if (!recaptchaSiteKey) {
    console.warn('reCAPTCHA site key not configured');
    return <OAuthAuthorize />;
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
      <OAuthAuthorize />
    </GoogleReCaptchaProvider>
  );
};

export default OAuthAuthorizeWithRecaptcha;
